import { useEffect, useMemo, useRef, useState } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { avatarSrc } from "../game/avatar";
import { cannedPhrases, cannedText, chatOff, isMuted, muted, setChatOff, toggleMute } from "../game/chat";
import type { ChatMessage } from "../gen/bazivan/v1/table_pb";
import { Overlay } from "./Overlay";
import { ask } from "./Dialog";

/**
 * چتِ میز — یک جریان برای بازیکن و تماشاچی.
 *
 * دو نما از یک داده: نوارِ گذرا روی میز (که خودش محو می‌شود و جلوی لمسِ
 * کارت را نمی‌گیرد) و برگه‌ی کامل با جعبه‌ی متن. دلیلش این است که بازیکنِ
 * وسطِ نوبت نباید برای دیدنِ یک «آفرین!» چیزی را باز کند، و کسی که می‌خواهد
 * بنویسد نباید با نواری که محو می‌شود سر کند.
 */

/**
 * جریانِ زنده‌ی پیام‌ها — همان کاری که لایوِ اینستاگرام می‌کند.
 *
 * تازه‌ترین پیام پایین می‌نشیند و با آمدنِ بعدی بالا می‌رود و کم‌رنگ‌تر
 * می‌شود، تا ته که کاملاً محو شود. پیامِ کهنه هم بعد از مدتی خودش می‌رود،
 * وگرنه صفحه‌ی ساکت با چند حبابِ ماسیده می‌مانْد.
 */
/** پیام‌هایی که باید دیده شوند: بی‌صداشده‌ها و حالتِ خاموش این‌جا فیلتر می‌شوند. */
function useVisibleChat(): readonly ChatMessage[] {
  const all = useSignal(client.chat);
  const off = useSignal(chatOff);
  useSignal(muted); // برای تازه‌شدن سرِ بی‌صدا کردن
  return useMemo(() => (off ? [] : all.filter((m) => !isMuted(m.playerId))), [all, off]);
}

const LIVE_MAX = 6;      // چند پیام هم‌زمان روی صفحه
const LIVE_MS = 14000;   // عمرِ یک پیام روی صفحه

/** پیامِ خودت پررنگ‌تر دیده شود — مثلِ هر چتی. */
function textOfMsg(m: ChatMessage): string {
  return m.canned ? cannedText(m.canned) : m.text;
}

export function ChatLive({ raised }: { raised: boolean }) {
  const msgs = useVisibleChat();
  const me = useSignal(client.playerId);
  const [now, setNow] = useState(() => Date.now());

  // حبابِ بی‌متن کشیده نمی‌شود: یک مستطیلِ خالی روی میز، خرابیِ بی‌توضیح
  // به نظر می‌رسد.
  const live = msgs
    .filter((m) => now - Number(m.atMs) < LIVE_MS && textOfMsg(m).trim() !== "")
    .slice(-LIVE_MAX);
  // ساعت فقط وقتی می‌زند که چیزی روی صفحه باشد.
  useEffect(() => {
    if (live.length === 0) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live.length]);

  if (live.length === 0) return null;
  return (
    <div className={"chatlive" + (raised ? " raised" : "")} aria-live="polite">
      {live.map((m, i) => (
        <div
          key={`${m.playerId}-${m.atMs}-${i}`}
          className={"bubble" + (m.playerId === me ? " mine" : "")}
          // فاصله از تازه‌ترین: ۰ یعنی همین الان آمد، هرچه بیشتر کم‌رنگ‌تر.
          style={{ ["--k" as string]: String(live.length - 1 - i) }}
        >
          <b>{m.name || "بازیکن"}</b>
          <span>{textOfMsg(m)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * نوارِ چتِ پایینِ صفحه — همیشه آماده، بی این‌که چیزی باز کنی.
 *
 * فقط برای تماشاچی: لبه‌ی پایینِ صفحه‌ی بازیکن دستِ خودش است و یک جعبه‌ی
 * متنِ همیشگی روی ورق‌هایش می‌نشیند. تماشاچی آن‌جا دستی ندارد.
 */
export function ChatBar({ chatOpen }: { chatOpen: boolean }) {
  const [text, setText] = useState("");
  const [chips, setChips] = useState(false);
  const off = useSignal(chatOff);
  const conn = useSignal(client.state);
  const err = useSignal(client.lastError);

  if (off) return null;
  if (!chatOpen) {
    return <div className="chatbar closed">میزبان چتِ این میز را بسته است</div>;
  }
  // اتصال که نیست، جعبه‌ی متن دروغ می‌گوید: نوشته می‌شود، پاک می‌شود، و هیچ‌جا
  // نمی‌رسد. به‌جایش همان چیزی گفته می‌شود که هست.
  if (conn !== "online") {
    return (
      <div className="chatbar closed">
        {conn === "reconnecting" || conn === "connecting"
          ? "در حالِ وصل‌شدن…"
          : err || "اتصال قطع است"}
      </div>
    );
  }

  // متن فقط وقتی پاک می‌شود که واقعاً رفته باشد؛ وگرنه دستِ نویسنده می‌مانَد.
  const send = () => { if (client.sendChat(text)) setText(""); };

  return (
    <>
      {chips && (
        <div className="chatchips">
          {cannedPhrases.map((p) => (
            <button key={p.key} className="chip"
                    onClick={() => { client.sendCanned(p.key); setChips(false); }}>
              {p.fa}
            </button>
          ))}
        </div>
      )}
      <div className="chatbar">
        <button className={"chip quick" + (chips ? " on" : "")} onClick={() => setChips((v) => !v)}
                aria-label="عبارت‌های آماده" title="عبارت‌های آماده">⚡</button>
        <input
          value={text}
          maxLength={200}
          placeholder="چیزی بنویس…"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
        />
        <button className="send" onClick={send} disabled={!text.trim()} aria-label="بفرست">➤</button>
      </div>
    </>
  );
}

/** برگه‌ی کامل: تاریخچه، عبارت‌های آماده، جعبه‌ی متن و تنظیم‌ها. */
export function ChatPanel({ open, onClose, chatOpen, host }:
  { open: boolean; onClose: () => void; chatOpen: boolean; host: boolean }) {
  const msgs = useVisibleChat();
  const off = useSignal(chatOff);
  const [text, setText] = useState("");
  const listEnd = useRef<HTMLDivElement>(null);

  // با هر پیامِ تازه به ته می‌چسبد — همان کاری که هر چتی می‌کند.
  useEffect(() => { if (open) listEnd.current?.scrollIntoView({ block: "end" }); }, [msgs.length, open]);

  if (!open) return null;

  const send = () => { if (client.sendChat(text)) setText(""); };

  return (
    // همان Overlay ِ بقیه‌ی پنجره‌ها: role=dialog، تمرکزِ آغازین و حبسِ تمرکز، Esc،
    // لمسِ بیرون و «بازگشت»ِ گوشی. پیش‌تر یک div بود که فقط Esc را می‌فهمید و
    // Tab از رویش به میزِ زیرِ پرده می‌رفت.
    <Overlay label="چتِ میز" className="chatwrap" onClose={onClose}>
    <div className="chatsheet">
      <div className="chathead">
        <b>چتِ میز</b>
        <div className="spacer" />
        {host && (
          <button className="chip" onClick={() => client.setChatOpen(!chatOpen)}>
            {chatOpen ? "بستنِ چت برای همه" : "بازکردنِ چت"}
          </button>
        )}
        <button className="chip" onClick={() => setChatOff(!off)}>
          {off ? "روشن‌کردنِ چت" : "خاموش برای من"}
        </button>
        <button className="chip" onClick={onClose} aria-label="بستن">✕</button>
      </div>

      <div className="chatlist">
        {off ? (
          <div className="muted" style={{ textAlign: "center", padding: 20 }}>
            چت را برای خودت خاموش کرده‌ای. کسی خبر ندارد.
          </div>
        ) : msgs.length === 0 ? (
          <div className="muted" style={{ textAlign: "center", padding: 20 }}>
            هنوز کسی چیزی نگفته.
          </div>
        ) : (
          msgs.map((m, i) => <Row key={`${m.playerId}-${m.atMs}-${i}`} m={m} />)
        )}
        <div ref={listEnd} />
      </div>

      {chatOpen ? (
        <>
          <div className="chatcanned">
            {cannedPhrases.map((p) => (
              <button key={p.key} className="chip" onClick={() => client.sendCanned(p.key)}>{p.fa}</button>
            ))}
          </div>
          <div className="row chatbox">
            <input
              value={text}
              maxLength={200}
              placeholder="چیزی بنویس…"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            />
            <button className="btn" style={{ width: "auto", paddingInline: 18 }} onClick={send} disabled={!text.trim()}>
              بفرست
            </button>
          </div>
        </>
      ) : (
        <div className="muted" style={{ textAlign: "center", padding: 12 }}>
          میزبان چتِ این میز را بسته است.
        </div>
      )}
    </div>
    </Overlay>
  );
}

/** یک پیام. لمسِ نام، آن نفر را برای من بی‌صدا می‌کند. */
function Row({ m }: { m: ChatMessage }) {
  const me = useSignal(client.playerId);
  const mine = m.playerId === me;
  return (
    <div className={`chatrow ${mine ? "mine" : ""}`}>
      <img className="seatface" src={avatarSrc(m.avatar)} alt="" width={26} height={26} />
      <div className="body">
        <button
          className="who"
          disabled={mine}
          title={mine ? undefined : "بی‌صدا کردنِ این نفر"}
          onClick={() => {
            if (mine) return;
            void ask({ title: `${m.name || "این نفر"} برای تو بی‌صدا شود؟`, body: "پیام‌هایش را فقط تو نمی‌بینی؛ خودش خبر ندارد.", ok: "بی‌صدا کن", cancel: "نه" })
              .then((yes) => { if (yes) toggleMute(m.playerId); });
          }}
        >
          {m.name || "بازیکن"}
          {/* تماشاچی صندلی ندارد؛ همین یک نشان کافی است تا معلوم شود از کنارِ میز حرف می‌زند */}
          {m.seat < 0 && <span className="tag">تماشاچی</span>}
        </button>
        <span className={m.canned ? "canned" : ""}>{textOfMsg(m)}</span>
      </div>
    </div>
  );
}
